import { useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { useFormContext } from "../../../context/FormContext";
import Button from "../../Button";

const LocationInput = ({ name, type, isRequired }) => {
  const { setFieldValue } = useFormikContext();
  const { setLocation, location } = useFormContext();
  const [status, setStatus] = useState("idle"); // "idle" | "loading" | "denied" | "failed" | "outsideOffice" | "success"
  const [errorMessage, setErrorMessage] = useState("");
  const retryCountRef = useRef(0);
  const watchIdRef = useRef(null);
  const MAX_RETRIES = 3;


  const OFFICE_LOCATION = {
    latitude: 6.451458324508796,
    longitude: 3.4703926019614766,
    radius: 100, // meters — feel free to increase to 150–250m for better indoor reliability
  };


  // Haversine formula to calculate distance in meters
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth radius in meters
    const lattitude1 = (lat1 * Math.PI) / 180;
    const lattitude2 = (lat2 * Math.PI) / 180;
    const lattitudeDiff = ((lat2 - lat1) * Math.PI) / 180;
    const longitudeDiff = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(lattitudeDiff / 2) * Math.sin(lattitudeDiff / 2) +
      Math.cos(lattitude1) * Math.cos(lattitude2) *
      Math.sin(longitudeDiff / 2) * Math.sin(longitudeDiff / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const isWithinOfficeRadius = (latitude, longitude) => {
    const distance = calculateDistance(
      latitude,
      longitude,
      OFFICE_LOCATION.latitude,
      OFFICE_LOCATION.longitude
    );
    return distance <= OFFICE_LOCATION.radius;
  };

  const geolocationOptions = {
    enableHighAccuracy: true,
    timeout: 20000,
    maximumAge: 0,
  };

  const handleSuccess = (position) => {
    const coords = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
    };

    if (isWithinOfficeRadius(coords.latitude, coords.longitude)) {
      setStatus("success");
      setErrorMessage("");
      retryCountRef.current = 0;
      setLocation(coords);
      setFieldValue(name, coords);

      // Stop continuous watching once we have a valid location
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    } else {
      setStatus("outsideOffice");
      const distanceAway = calculateDistance(
        coords.latitude,
        coords.longitude,
        OFFICE_LOCATION.latitude,
        OFFICE_LOCATION.longitude
      ).toFixed(0);

      setErrorMessage(
        `You are currently ${distanceAway} meters away from the office.\n` +
        `You must be within ${OFFICE_LOCATION.radius} meters to proceed.`
      );
      setFieldValue(name, "");
      // Keep watching — user might walk into the radius
    }
  };

  const handleError = (error) => {
    console.error("Geolocation error:", error.code, error.message);

    const isPermissionDenied = error.code === 1;
    const isTransientError =
      error.code === 2 ||
      error.code === 3 ||
      error.message?.includes("kCLErrorLocationUnknown") ||
      error.message?.includes("locationUnknown");

    if (isPermissionDenied) {
      setStatus("denied");
      setErrorMessage("Location permission is required to verify you're at the office.");
      setFieldValue(name, "");
      return;
    }

    if (isTransientError && retryCountRef.current < MAX_RETRIES) {
      retryCountRef.current += 1;
      setStatus("loading");
      setErrorMessage(`Retrying location... (${retryCountRef.current}/${MAX_RETRIES})`);
      const delay = 2000 * Math.pow(2, retryCountRef.current - 1);
      setTimeout(startLocationRequest, delay);
      return;
    }

    setStatus("failed");
    setErrorMessage("Unable to verify location. Please check that location services are enabled.");
    setFieldValue(name, "");
  };

  const startLocationRequest = () => {
    if (!navigator.geolocation) {
      setStatus("failed");
      setErrorMessage("Geolocation is not supported by this browser.");
      return;
    }

    setStatus("loading");
    setErrorMessage("Verifying your location...");

    // One-time request
    navigator.geolocation.getCurrentPosition(handleSuccess, handleError, geolocationOptions);

    // Continuous watching (useful if user moves into radius)
    watchIdRef.current = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      geolocationOptions
    );
  };

  useEffect(() => {
    startLocationRequest();

    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  const retryManual = () => {
    retryCountRef.current = 0;
    startLocationRequest();
  };
  
  //Modals

  const renderOutsideOfficeModal = () => (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ marginBottom: "1.5rem", textAlign: "center" }}>
          <div style={iconCircleStyle}>
            <span style={{ fontSize: "36px" }}>📍</span>
          </div>
          <h3 style={{ color: "#d32f2f", margin: "0.75rem 0 0.25rem" }}>
            Office Location Required
          </h3>
          <p style={{ color: "#555", margin: "0" }}>
            You must be physically at the office to use this form.
          </p>
        </div>

        <div style={infoBoxStyle}>
          <p style={{ margin: "0 0 0.6rem", fontWeight: "bold" }}>Office Details:</p>
          <ul style={{ margin: 0, paddingLeft: "1.4rem", lineHeight: 1.5 }}>
            <li>Coordinates: {OFFICE_LOCATION.latitude.toFixed(6)}, {OFFICE_LOCATION.longitude.toFixed(6)}</li>
            <li>Allowed radius: {OFFICE_LOCATION.radius} meters</li>
            <li>Status: <strong style={{ color: "#d32f2f" }}>Outside area</strong></li>
          </ul>
        </div>

        <p style={{ margin: "1.2rem 0", color: "#444", whiteSpace: "pre-line" }}>
          {errorMessage}
        </p>

        <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
          <Button
            type="button"
            onClick={retryManual}
            style={{ ...btnStyle, backgroundColor: "#1976d2", color: "white" }}
          >
            Re-check Location
          </Button>
          <Button
            type="button"
            style={{ ...btnStyle, backgroundColor: "#757575", color: "white" }}
          >
            Contact Support
          </Button>
        </div>
      </div>
    </div>
  );

  const renderErrorModal = () => (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <p style={{ fontWeight: "bold", fontSize: "1.2rem", marginBottom: "1rem" }}>
          {status === "denied" ? "Location Permission Required" : "Location Error"}
        </p>
        <p style={{ marginBottom: "1.5rem" }}>{errorMessage}</p>

        {status === "denied" && (
          <ul style={{ textAlign: "left", paddingLeft: "1.6rem", marginBottom: "1.5rem", lineHeight: 1.5 }}>
            <li>Click the lock / info icon next to the URL</li>
            <li>Go to Site Settings → Permissions</li>
            <li>Allow Location access</li>
            <li>Refresh the page or click below</li>
          </ul>
        )}

        <Button
          type="button"
          onClick={retryManual}
          style={{ ...btnStyle, backgroundColor: status === "denied" ? "#2e7d32" : "#f57c00", color: "white" }}
        >
          {status === "denied" ? "Allow & Retry" : "Try Again"}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {status === "loading" && (
        <div style={loadingBannerStyle}>
          <div style={pulseIconStyle}>📍</div>
          <p style={{ margin: "0.5rem 0 0", fontWeight: "bold" }}>
            Verifying you're at the office...
          </p>
          <p style={{ margin: "0.4rem 0 0", fontSize: "0.95rem", opacity: 0.9 }}>
            {errorMessage || "Please wait"}
          </p>
        </div>
      )}

      {status === "outsideOffice" && renderOutsideOfficeModal()}
      {(status === "denied" || status === "failed") && renderErrorModal()}

      {status === "success" && location && (
        <div style={successBoxStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
            <span style={{ fontSize: "24px" }}>✅</span>
            <div>
              <p style={{ margin: 0, fontWeight: "bold", color: "#1b5e20" }}>
                Location Verified
              </p>
              <p style={{ margin: "0.2rem 0 0", fontSize: "0.9rem", color: "#555" }}>
                You're within the office area
                {location.accuracy && ` (±${location.accuracy.toFixed(0)}m)`}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ────────────────────────────────────────────────
//                     STYLES
// ────────────────────────────────────────────────

const overlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.75)",
  backdropFilter: "blur(8px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
};

const modalStyle = {
  width: "90%",
  maxWidth: "480px",
  background: "#ffffff",
  borderRadius: "16px",
  padding: "2rem",
  boxShadow: "0 20px 70px rgba(0,0,0,0.35)",
};

const iconCircleStyle = {
  width: "70px",
  height: "70px",
  borderRadius: "50%",
  backgroundColor: "#ffebee",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 1rem",
};

const infoBoxStyle = {
  backgroundColor: "#f9f9f9",
  borderRadius: "10px",
  padding: "1.2rem",
  marginBottom: "1.4rem",
};

const btnStyle = {
  padding: "0.8rem 1.8rem",
  borderRadius: "8px",
  fontWeight: "600",
  border: "none",
  cursor: "pointer",
  fontSize: "1rem",
};

const loadingBannerStyle = {
  padding: "1.8rem",
  textAlign: "center",
  background: "linear-gradient(135deg, #667eea, #764ba2)",
  color: "white",
  borderRadius: "12px",
  margin: "1.5rem 0",
};

const pulseIconStyle = {
  fontSize: "2.8rem",
  marginBottom: "0.8rem",
  animation: "pulse 2s infinite",
};

const successBoxStyle = {
  padding: "1.2rem",
  backgroundColor: "#E9F0FE",
  border: "1px solid #1F1E52",
  borderRadius: "10px",
  margin: "1.5rem 0",
};

// Add animations to head (only once)
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = `
    @keyframes pulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50%      { transform: scale(1.15); opacity: 0.75; }
    }
  `;
  document.head.appendChild(style);
}

export default LocationInput;